/**
 * Crédit Client Intelligent — le différenciateur #1 Afrique.
 *
 * Responsabilités :
 * - Scorer un client 0..100 selon son historique (local, privacy-first).
 * - Générer les relances (J+3, J+7, J+15).
 * - Décider du blocage automatique (score trop bas OU 2 relances sans réponse).
 * - Calculer la balance âgée (0-30 / 31-60 / 61-90 / 90+).
 * - Décider si une nouvelle vente à crédit est autorisée.
 *
 * Algorithme de scoring (v1 — local, pas de ML) :
 * - Base 50 (neutre).
 * - + ancienneté (max +15) : 1pt par mois d'ancienneté, capé à 15.
 * - + ponctualité (max +25) : ratio paiements à l'heure / total échéances.
 * - + fréquence (max +10) : nb_achats / 10, capé à 10.
 * - - retards (max -30) : somme des jours de retard, capé à 30.
 * - - impayés (max -50) : 1 crédit en statut 'bloque' = -25 chacun.
 *
 * 0-30 = bloqué (refus crédit).
 * 31-50 = surveillance (relances J+3).
 * 51-75 = ok (relances J+7).
 * 76-100 = bon client (relances J+15).
 */

import { DomainError } from '../money.js';
import { ok, err, type Result } from '../result.js';
import { diffDays, isOverdue } from '../time.js';

// ============================================================
// Types
// ============================================================

export interface Client {
  readonly id: string;
  readonly boutiqueId: string;
  readonly nom: string;
  readonly tel?: string;
  readonly scoring: number; // 0..100
  readonly nbVisites: number;
  readonly totalAchats: number; // FCFA cumulé
  readonly derniereVisiteAt: number;
  readonly bloque: boolean; // true = refus automatique de crédit
}

export interface Credit {
  readonly id: string;
  readonly clientId: string;
  readonly boutiqueId: string;
  readonly venteId: string;
  readonly montant: number; // FCFA
  readonly paye: number; // FCFA
  readonly echeanceAt: number; // epoch ms
  readonly statut: 'ouvert' | 'relance' | 'bloque' | 'paye';
  readonly scoringAuMoment: number;
  readonly createdAt: number;
}

export interface HistoriquePaiement {
  readonly creditId: string;
  readonly echeanceAt: number;
  readonly payeAt: number | null; // null = pas encore payé
}

export type NiveauRisque = 'eleve' | 'moyen' | 'faible' | 'bon';

export interface DecisionCredit {
  readonly autorise: boolean;
  readonly motif: string;
  readonly niveau: NiveauRisque;
  readonly montantMaxConseille?: number; // FCFA
}

// ============================================================
// Calcul du scoring
// ============================================================

export interface ScoringInput {
  readonly ancienneteJours: number;
  readonly nbVisites: number;
  readonly totalAchats: number; // FCFA
  readonly creditsEnRetard: number; // nb crédits en retard
  readonly creditsBloques: number; // nb crédits en statut 'bloque'
  readonly joursRetardCumules: number; // somme des jours de retard
  readonly ratioPaiementsATemps: number; // 0..1
}

export const calculerScoring = (input: ScoringInput): number => {
  let score = 50;

  // + ancienneté (max +15) : 1pt par tranche de 30 jours
  score += Math.min(15, Math.floor(input.ancienneteJours / 30));

  // + ponctualité (max +25)
  score += Math.round(input.ratioPaiementsATemps * 25);

  // + fréquence (max +10) : 1pt par visite, capé à 10
  score += Math.min(10, Math.floor(input.nbVisites / 1));

  // - retards (max -30)
  score -= Math.min(30, input.joursRetardCumules);

  // - impayés (max -50)
  score -= Math.min(50, input.creditsBloques * 25);

  // Bonus volume (max +5) : si totalAchats > 500 000 F
  if (input.totalAchats > 500_000) score += 5;

  return Math.max(0, Math.min(100, score));
};

export const niveauRisque = (score: number): NiveauRisque => {
  if (score <= 30) return 'eleve';
  if (score <= 50) return 'moyen';
  if (score <= 75) return 'faible';
  return 'bon';
};

// ============================================================
// Décision d'octroi de crédit
// ============================================================

export interface OctroiInput {
  readonly client: Client;
  readonly montantDemande: number; // FCFA
  readonly encoursActuel: number; // total des crédits ouverts non payés
  readonly now: number;
}

export const deciderOctroi = (input: OctroiInput): DecisionCredit => {
  // Client bloqué → refus dur
  if (input.client.bloque) {
    return {
      autorise: false,
      motif: 'Client bloqué (impayés précédents)',
      niveau: 'eleve',
    };
  }

  // Score très bas → refus
  if (input.client.scoring <= 30) {
    return {
      autorise: false,
      motif: `Score trop bas (${input.client.scoring}/100)`,
      niveau: 'eleve',
    };
  }

  // Premier achat à crédit sans historique → OK mais plafond bas
  if (input.client.nbVisites < 3) {
    return {
      autorise: true,
      motif: 'Nouveau client — plafond de départ',
      niveau: 'moyen',
      montantMaxConseille: Math.min(input.montantDemande, 5_000),
    };
  }

  // Plafond d'encours selon scoring
  const plafond = calculPlafond(input.client.scoring);
  const encoursFutur = input.encoursActuel + input.montantDemande;

  if (encoursFutur > plafond) {
    return {
      autorise: false,
      motif: `Encours max (${plafond} F) dépassé (actuel ${input.encoursActuel} F)`,
      niveau: input.client.scoring <= 50 ? 'eleve' : 'moyen',
    };
  }

  return {
    autorise: true,
    motif: 'OK',
    niveau: niveauRisque(input.client.scoring),
    montantMaxConseille: plafond - input.encoursActuel,
  };
};

const calculPlafond = (score: number): number => {
  if (score >= 90) return 500_000;
  if (score >= 75) return 200_000;
  if (score >= 60) return 100_000;
  if (score >= 45) return 50_000;
  return 20_000;
};

// ============================================================
// Échéances et relances
// ============================================================

export interface RelancePlanifiee {
  readonly creditId: string;
  readonly clientId: string;
  readonly type: 'rappel_ami' | 'rappel_ferme' | 'mise_en_demeure' | 'blocage';
  readonly joursAvantEcheance: number; // négatif = après échéance
  readonly envoyerAt: number;
  readonly template: string;
}

/**
 * Planifie les relances pour un crédit :
 * - J-2 : rappel ami (avant échéance)
 * - J+3 : rappel ferme
 * - J+7 : mise en demeure
 * - J+15 : blocage automatique
 */
export const planifierRelances = (
  credit: Credit,
  now: number,
): RelancePlanifiee[] => {
  const J = 86_400_000;
  const echeance = credit.echeanceAt;

  const plan: RelancePlanifiee[] = [
    {
      creditId: credit.id,
      clientId: credit.clientId,
      type: 'rappel_ami',
      joursAvantEcheance: -2,
      envoyerAt: echeance - 2 * J,
      template: 'ami',
    },
    {
      creditId: credit.id,
      clientId: credit.clientId,
      type: 'rappel_ferme',
      joursAvantEcheance: 3,
      envoyerAt: echeance + 3 * J,
      template: 'ferme',
    },
    {
      creditId: credit.id,
      clientId: credit.clientId,
      type: 'mise_en_demeure',
      joursAvantEcheance: 7,
      envoyerAt: echeance + 7 * J,
      template: 'demeure',
    },
    {
      creditId: credit.id,
      clientId: credit.clientId,
      type: 'blocage',
      joursAvantEcheance: 15,
      envoyerAt: echeance + 15 * J,
      template: 'blocage',
    },
  ];
  return plan.filter(r => r.envoyerAt > now);
};

/** Détermine la prochaine relance à envoyer. */
export const prochaineRelance = (
  credit: Credit,
  relancesDejaEnvoyees: ReadonlyArray<string>, // types déjà envoyés
  now: number,
): RelancePlanifiee | null => {
  const plan = planifierRelances(credit, now);
  return plan.find(r => !relancesDejaEnvoyees.includes(r.type)) ?? null;
};

// ============================================================
// Blocage automatique
// ============================================================

export const doitBloquer = (
  credit: Credit,
  now: number,
): boolean => {
  // Bloqué si +15 jours de retard ET pas encore marqué 'bloque' ni 'paye'
  if (credit.statut === 'paye' || credit.statut === 'bloque') return false;
  const retardJours = diffDays(credit.echeanceAt, now);
  return retardJours >= 15 && credit.paye < credit.montant;
};

// ============================================================
// Balance âgée
// ============================================================

export type TrancheBalance = '0-30' | '31-60' | '61-90' | '90+';

export interface BalanceAgee {
  readonly tranches: Record<TrancheBalance, { montant: number; nbCredits: number }>;
  readonly total: number;
}

/**
 * Calcule la balance âgée = répartition des crédits impayés par tranche de retard.
 * Standard comptable SYSCOHADA / OHADA.
 */
export const calculerBalanceAgee = (
  credits: ReadonlyArray<Credit>,
  now: number,
): BalanceAgee => {
  const tranches: Record<TrancheBalance, { montant: number; nbCredits: number }> = {
    '0-30': { montant: 0, nbCredits: 0 },
    '31-60': { montant: 0, nbCredits: 0 },
    '61-90': { montant: 0, nbCredits: 0 },
    '90+': { montant: 0, nbCredits: 0 },
  };

  for (const c of credits) {
    if (c.statut === 'paye') continue;
    const reste = c.montant - c.paye;
    if (reste <= 0) continue;
    const retard = Math.max(0, diffDays(c.echeanceAt, now));
    const tranche: TrancheBalance =
      retard <= 30 ? '0-30' :
      retard <= 60 ? '31-60' :
      retard <= 90 ? '61-90' : '90+';
    tranches[tranche].montant += reste;
    tranches[tranche].nbCredits += 1;
  }

  const total = Object.values(tranches).reduce<number>((acc, t) => acc + t.montant, 0);
  return { tranches, total };
};

// ============================================================
// Création d'un crédit depuis une vente
// ============================================================

export interface CreerCreditInput {
  readonly client: Client;
  readonly venteId: string;
  readonly montant: number; // FCFA
  readonly echeanceJours: number; // ex: 30 pour "à payer dans 30 jours"
  readonly now: number;
}

export const creerCredit = (input: CreerCreditInput): Result<Credit> => {
  if (!Number.isInteger(input.montant) || input.montant <= 0) {
    return err(new DomainError('montant_invalide', `Montant crédit invalide: ${input.montant}`));
  }
  if (input.echeanceJours < 1 || input.echeanceJours > 365) {
    return err(
      new DomainError(
        'echeance_invalide',
        `Échéance invalide: ${input.echeanceJours} jours (1-365)`,
      ),
    );
  }
  if (input.client.bloque) {
    return err(
      new DomainError(
        'client_bloque',
        'Impossible de créer un crédit pour un client bloqué',
      ),
    );
  }

  return ok({
    id: '', // sera rempli par la couche persistance (newId())
    clientId: input.client.id,
    boutiqueId: input.client.boutiqueId,
    venteId: input.venteId,
    montant: input.montant,
    paye: 0,
    echeanceAt: input.now + input.echeanceJours * 86_400_000,
    statut: 'ouvert',
    scoringAuMoment: input.client.scoring,
    createdAt: input.now,
  });
};

// ============================================================
// Paiement partiel / total d'un crédit
// ============================================================

export interface PaiementCreditInput {
  readonly credit: Credit;
  readonly montant: number; // FCFA
  readonly mode: 'cash' | 'momo' | 'orange' | 'moov';
  readonly momoRef?: string;
  readonly now: number;
}

export const appliquerPaiementCredit = (
  input: PaiementCreditInput,
): Result<Credit> => {
  if (!Number.isInteger(input.montant) || input.montant <= 0) {
    return err(new DomainError('montant_invalide', `Montant paiement invalide: ${input.montant}`));
  }
  if (input.credit.statut === 'paye') {
    return err(new DomainError('credit_deja_paye', 'Ce crédit est déjà soldé'));
  }

  const nouveauPaye = input.credit.paye + input.montant;
  const solde = nouveauPaye >= input.credit.montant;

  return ok({
    ...input.credit,
    paye: nouveauPaye,
    statut: solde ? 'paye' : input.credit.statut,
    updatedAt: input.now,
  });
};

// ============================================================
// Templates de messages WhatsApp (relances)
// ============================================================

export const templateRelance = (
  type: RelancePlanifiee['type'],
  vars: { nom: string; montant: number; boutiqueNom: string; joursRetard: number },
): string => {
  // Format simple avec espace normal (pas d'espace fine typographique)
  const montantFormate = new Intl.NumberFormat('fr-BJ', { useGrouping: true })
    .format(vars.montant)
    .replace(/ | /g, ' ');

  switch (type) {
    case 'rappel_ami':
      return `Bonjour ${vars.nom} 👋 Petit rappel amical : ta dette de ${montantFormate} F chez ${vars.boutiqueNom} arrive à échéance dans 2 jours. Merci 🙏`;

    case 'rappel_ferme':
      return `Bonjour ${vars.nom}, ta dette de ${montantFormate} F chez ${vars.boutiqueNom} a ${vars.joursRetard} jours de retard. Merci de régulariser rapidement. — ${vars.boutiqueNom}`;

    case 'mise_en_demeure':
      return `${vars.nom}, sauf règlement de ${montantFormate} F sous 7 jours, ${vars.boutiqueNom} se réserve le droit de suspendre tout nouveau crédit. — Mise en demeure`;

    case 'blocage':
      return `${vars.nom}, ton compte chez ${vars.boutiqueNom} est suspendu pour impayé (${montantFormate} F). Contacte-nous pour régulariser.`;

    default:
      return '';
  }
};
